import React, {
  useEffect,
  useState,
  Fragment,
  useRef,
  useCallback,
} from "react";
import styles from "../../../style/components/chat/ChatView.module.css";
import ChatShimmer from "../../ui/Loader/Shimmer/ChatShimmer";
import NoChatFound from "../../ui/NoDataFound/NoChatFound";
import { GrDownload } from "react-icons/gr";
import { BsEmojiSmile } from "react-icons/bs";
import { FaPlus } from "react-icons/fa";
import EmojiPicker from "emoji-picker-react";
import WebSocketService from "../../../services/websocket";
import { getRegisterId } from "../../../utils/authId";
import { Emoji } from "emoji-picker-react";
import { renderTextWithEmojis } from "../../../utils/renderTextWithEmojis";
import { getUserDetails } from "../../../utils/auth";
import FallbackAvatar from "../../ui/FallbackAvatar/FallbackAvatar";
import ImagePreview from "./ImagePreview";
import PersonQuickView from "../../../Pages/chat/PopUp/PersonQuickView";
import { PiShareFat } from "react-icons/pi";
import { RiDeleteBinLine } from "react-icons/ri";
import { canUserDelete } from "../../../utils/deleteAccess";
import { TfiPinAlt } from "react-icons/tfi";
import { RiUnpinLine } from "react-icons/ri";

const parseDelta = (delta, messageId) => {
  if (!Array.isArray(delta)) return null;

  const cleanedDelta = [...delta];
  if (cleanedDelta.length > 0) {
    const lastOp = cleanedDelta[cleanedDelta.length - 1];
    if (lastOp.insert && typeof lastOp.insert === "string") {
      lastOp.insert = lastOp.insert.replace(/\n+$/, "");
    }
  }

  return cleanedDelta.map((op, opIndex) => {
    if (!op.insert || typeof op.insert !== "string") return null;

    let content = renderTextWithEmojis(op.insert, `${messageId}-${opIndex}`);

    if (op.attributes) {
      if (op.attributes.underline) {
        content = <u key={`u-${messageId}-${opIndex}`}>{content}</u>;
      }
      if (op.attributes.italic) {
        content = <em key={`em-${messageId}-${opIndex}`}>{content}</em>;
      }
      if (op.attributes.bold) {
        content = <strong key={`b-${messageId}-${opIndex}`}>{content}</strong>;
      }
      if (op.attributes.mention) {
        content = (
          <span
            key={`mention-${messageId}-${opIndex}`}
            className={styles.mentionText}
          >
            {content}
          </span>
        );
      }
    }

    return <Fragment key={`frag-${messageId}-${opIndex}`}>{content}</Fragment>;
  });
};

const ChatView = ({
  newReaction,
  messages,
  chatViewRef,
  isLoading,
  height,
  pendingMessages,
  chat_unique,
  topObserverRef,
  isFetchingNextPage,
  hasNextPage,
  onForwardClick,
  onDeleteClick,
  onPinClick,
}) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const prevMessagesLengthRef = useRef(0);
  const hasUserManuallyScrolledRef = useRef(false);
  const [showTopNewMessageIndicator, setShowTopNewMessageIndicator] =
    useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  const socketMessages = React.useMemo(() => {
    return messages.filter((msg) => msg.source === "socket_receive");
  }, [messages]);

  const prevSocketMessagesCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0 && isInitialRender) {
      prevMessagesLengthRef.current = messages.length;
      prevSocketMessagesCountRef.current = socketMessages.length;
      setIsInitialRender(false);
    }
  }, [messages.length, isInitialRender, socketMessages.length]);

  const handleScroll = useCallback(() => {
    if (!chatViewRef?.current || isInitialRender) return;

    const chatContainer = chatViewRef.current;
    const { scrollTop, scrollHeight, clientHeight } = chatContainer;

    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;

    setAutoScroll(isAtBottom);

    if (isAtBottom) {
      setLastMessageCount(0);
      setShowTopNewMessageIndicator(false);
      hasUserManuallyScrolledRef.current = false;
      hasUserManuallyScrolledRef.current = false;
    } else {
      hasUserManuallyScrolledRef.current = true;
    }
  }, [chatViewRef, isInitialRender, setLastMessageCount]);

  useEffect(() => {
    const chatContainer = chatViewRef?.current;
    if (!chatContainer) return;

    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, [handleScroll, chatViewRef]);

  useEffect(() => {
    if (!chatViewRef?.current || isLoading || isInitialRender) return;

    const currentSocketMessagesCount = socketMessages.length;
    const previousSocketMessagesCount = prevSocketMessagesCountRef.current;

    if (currentSocketMessagesCount > previousSocketMessagesCount) {
      const newMessageCount =
        currentSocketMessagesCount - previousSocketMessagesCount;

      const chatContainer = chatViewRef.current;
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (!isAtBottom && hasUserManuallyScrolledRef.current) {
        setLastMessageCount((prev) => prev + newMessageCount);
        setShowTopNewMessageIndicator(true);
      } else if (isAtBottom) {
        setLastMessageCount(0);
        setShowTopNewMessageIndicator(false);
      }
    }

    prevMessagesLengthRef.current = messages.length;
    prevSocketMessagesCountRef.current = currentSocketMessagesCount;
  }, [
    messages.length,
    socketMessages.length,
    isLoading,
    chatViewRef,
    isInitialRender,
    setLastMessageCount,
  ]);

  useEffect(() => {
    if (autoScroll && chatViewRef?.current && !isLoading) {
      chatViewRef.current.scrollTop = chatViewRef.current.scrollHeight;
      setLastMessageCount(0);
      setShowTopNewMessageIndicator(false);
      hasUserManuallyScrolledRef.current = false;
    }
  }, [
    messages.length,
    autoScroll,
    isLoading,
    chatViewRef,
    setLastMessageCount,
  ]);

  const scrollToBottom = () => {
    if (chatViewRef?.current) {
      chatViewRef.current.scrollTop = chatViewRef.current.scrollHeight;
      setLastMessageCount(0);
      setShowTopNewMessageIndicator(false);
      setAutoScroll(true);
      hasUserManuallyScrolledRef.current = false;
    }
  };
  useEffect(() => {
    setLastMessageCount(0);
    setShowTopNewMessageIndicator(false);
    setAutoScroll(true);
    setIsInitialRender(true);
    hasUserManuallyScrolledRef.current = false;
    prevMessagesLengthRef.current = 0;
    prevSocketMessagesCountRef.current = 0;
  }, [chat_unique, setLastMessageCount]);

  const processedMessages =
    messages
      ?.filter((msg) => msg.text?.trim() || (msg.file && msg.file.length > 0))
      .map((message, index, array) => {
        if (index === 0) {
          return { ...message, showHeader: true };
        }

        const prevMessage = array[index - 1];
        const isSameUser = prevMessage.userName === message.userName;
        const isSameMinute = isTimeInSameMinute(prevMessage.time, message.time);

        return {
          ...message,
          showHeader: !(isSameUser && isSameMinute),
        };
      }) || [];

  const groupedMessages = processedMessages.reduce((acc, message) => {
    const date = message.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(message);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedMessages).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  if (isLoading) return <ChatShimmer />;
  if (!messages?.length && !pendingMessages) return <NoChatFound />;

  return (
    <div
      className={styles.ChatViewMaincontentChatview}
      style={{
        maxHeight: `calc(100% - ${height + 95}px)`,
        height: `calc(100% - ${height + 95}px)`,
      }}
    >
      {" "}
      {showTopNewMessageIndicator && lastMessageCount > 0 && (
        <div className={styles.newMessageIndicator} onClick={scrollToBottom}>
          <span className={styles.newMessageBadge}>{lastMessageCount}</span>
          <span className={styles.newMessageText}>
            New message{lastMessageCount > 1 ? "s" : ""}
          </span>
          <span className={styles.newMessageArrow}>↓</span>
        </div>
      )}
      <div className={styles.ChatViewMaincontentChatviewsb} ref={chatViewRef}>
        {isFetchingNextPage && (
          <div className={styles.topLoadingIndicator}>
            <div className={styles.loadingSpinner}></div>
            <span>Loading older messages...</span>
          </div>
        )}

        <div
          ref={topObserverRef}
          className={styles.loadMoreTrigger}
          style={{
            height: "1px",
            visibility: hasNextPage ? "visible" : "hidden",
          }}
        ></div>
        {sortedDates.map((date) => (
          <div key={date}>
            <div className={styles.ChatViewDateSeparator}>
              <span>
                {new Date(date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>

            {groupedMessages[date].map((message, index) => (
              <Message
                key={`${date}-${index}`}
                {...message}
                newReaction={newReaction}
                chat_unique={chat_unique}
                chatViewRef={chatViewRef}
                onForwardClick={onForwardClick}
                onDeleteClick={onDeleteClick}
                onPinClick={onPinClick}
              />
            ))}
          </div>
        ))}
        {pendingMessages && (
          <div className={styles.customLoader}>
            <div className={styles.loaderBubble}>
              <div className={styles.loaderDots}>
                <div className={styles.dot}></div>
                <div className={styles.dot}></div>
                <div className={styles.dot}></div>
              </div>
              <span className={styles.customLoaderpara}>Sending...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const isTimeInSameMinute = (time1, time2) => {
  if (!time1 || !time2) return false;

  const extractTimePart = (timeStr) => {
    const timeMatch = timeStr.match(/(\d{1,2}:\d{2})/);
    return timeMatch ? timeMatch[1] : timeStr;
  };

  const timePart1 = extractTimePart(time1);
  const timePart2 = extractTimePart(time2);

  return timePart1 === timePart2;
};
const Message = ({
  id,
  isReceived,
  userName,
  text,
  file,
  time,
  avatar,
  reactions,
  newReaction,
  chat_unique,
  chatViewRef,
  user_id,
  showHeader = true,
  onForwardClick,
  onDeleteClick,
  is_forward,
  is_you,
  is_pinned,
  onPinClick,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const commonReactions = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
  const [showEmojiPopup, setShowEmojiPopup] = useState(false);
  const currentUserId = getRegisterId();
  const [hoveredReaction, setHoveredReaction] = useState(null);
  const userDetails = getUserDetails();
  const [imgError, setImgError] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [disableScroll, setDisableScroll] = useState(false);
  const scrollPositionRef = useRef(0);
  const [showPersonPopUp, setShowPersonPopUp] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const showDeleteButton = canUserDelete();

  const [transformedReactions, setTransformedReactions] = useState(
    reactions?.map((reaction) => ({
      emoji: reaction.react_emoji,
      count: reaction.reaction_count,
      userReacted: reaction.react_people_names.some(
        (user) => user.register_id === currentUserId
      ),
      people: reaction.react_people_names,
    })) || []
  );
  useEffect(() => {
    const chatContainer = chatViewRef?.current;
    if (!chatContainer) return;

    const handleScroll = (e) => {
      if (disableScroll) {
        e.preventDefault();
        e.stopPropagation();
        chatContainer.scrollTop = scrollPositionRef.current;
        return false;
      }
    };

    chatContainer.addEventListener("scroll", handleScroll, { passive: false });

    return () => {
      chatContainer.removeEventListener("scroll", handleScroll);
    };
  }, [disableScroll, chatViewRef]);
  const handleForwardClick = () => {
    if (onForwardClick) {
      onForwardClick(id, chat_unique);
    }
  };
  const handleDeleteClick = () => {
    if (onDeleteClick) {
      onDeleteClick(id, chat_unique);
    }
  };

  const handlePinClick = () => {
    if (onPinClick) {
      onPinClick(id, chat_unique, is_pinned);
    }
  };

  useEffect(() => {
    if (newReaction && newReaction.reaction?.message_id === id) {
      setTransformedReactions((prev) => {
        const existing = prev.find(
          (r) => r.emoji === newReaction.reaction.emoji
        );

        if (newReaction.action === "add") {
          if (existing) {
            return prev.map((r) =>
              r.emoji === newReaction.reaction.emoji
                ? {
                    ...r,
                    count: r.count + 1,
                    userReacted:
                      r.people.some(
                        (user) => user.register_id === currentUserId
                      ) || r.userReacted,
                    people: [
                      ...new Set([
                        ...r.people,
                        ...newReaction.reaction.react_people_names,
                      ]),
                    ],
                  }
                : r
            );
          } else {
            return [
              ...prev,
              {
                emoji: newReaction.reaction.emoji,
                count: 1,
                userReacted: newReaction.reaction.react_people_names.some(
                  (user) => user.register_id === currentUserId
                ),
                people: newReaction.reaction.react_people_names || [],
              },
            ];
          }
        } else if (newReaction.action === "remove") {
          const senderId = newReaction.sender_id;

          return prev
            .map((r) =>
              r.emoji === newReaction.reaction.emoji
                ? {
                    ...r,
                    count: r.count - 1,
                    userReacted: r.people.some(
                      (user) =>
                        user.register_id === currentUserId &&
                        user.register_id !== senderId
                    ),
                    people: r.people.filter(
                      (user) => user.register_id !== senderId
                    ),
                  }
                : r
            )
            .filter((r) => r.count > 0);
        }

        return prev;
      });
    }
  }, [newReaction, id, currentUserId]);

  const handleAddReaction = (emoji) => {
    scrollPositionRef.current = chatViewRef?.current?.scrollTop;

    setDisableScroll(true);
    WebSocketService.sendReaction(id, chat_unique, emoji, "add");

    setTransformedReactions((prev) => {
      const existingReaction = prev.find((r) => r.emoji === emoji);

      if (existingReaction) {
        return prev.map((r) =>
          r.emoji === emoji
            ? {
                ...r,
                count: r.count + 1,
                userReacted: true,
                people: [
                  ...r.people,
                  { register_id: currentUserId, name: userDetails.name },
                ],
              }
            : r
        );
      } else {
        return [
          ...prev,
          {
            emoji,
            count: 1,
            userReacted: true,
            people: [{ register_id: currentUserId, name: userDetails.name }],
          },
        ];
      }
    });

    setShowReactionPicker(false);
    setTimeout(() => {
      setDisableScroll(false);
      if (chatViewRef?.current) {
        chatViewRef.current.scrollTop = scrollPositionRef.current;
      }
    }, 100);
  };

  const handleRemoveReaction = (emoji) => {
    scrollPositionRef.current = chatViewRef?.current?.scrollTop;

    setDisableScroll(true);
    WebSocketService.sendReaction(id, chat_unique, emoji, "remove");

    setTransformedReactions((prev) =>
      prev
        .map((r) =>
          r.emoji === emoji
            ? {
                ...r,
                count: r.count - 1,
                userReacted: false,
                people: r.people.filter(
                  (user) => user.register_id !== currentUserId
                ),
              }
            : r
        )
        .filter((r) => r.count > 0)
    );
    setTimeout(() => {
      setDisableScroll(false);
      if (chatViewRef?.current) {
        chatViewRef.current.scrollTop = scrollPositionRef.current;
      }
    }, 100);
  };

  const toggleReaction = (emoji) => {
    const existingReaction = transformedReactions.find(
      (r) => r.emoji === emoji
    );
    if (
      existingReaction?.people.some(
        (user) => user.register_id === currentUserId
      )
    ) {
      handleRemoveReaction(emoji);
    } else {
      handleAddReaction(emoji);
    }
  };

  const getFileType = (extension) => {
    extension = extension.toLowerCase();
    const typeMap = {
      doc: "word",
      docx: "word",
      pdf: "pdf",
      jpg: "image",
      jpeg: "image",
      png: "image",
      gif: "image",
      xls: "excel",
      xlsx: "excel",
      ppt: "powerpoint",
      pptx: "powerpoint",
      zip: "zip",
      rar: "zip",
      js: "js",
      py: "py",
      ts: "ts",
      jsx: "jsx",
      html: "html",
      css: "css",
      "7z": "zip",
      csv: "csv",
      pyz: "zip",
      java: "java",
      php: "php",
      txt: "txt",
      mp4: "video",
      mov: "video",
      avi: "video",
      mkv: "video",
      wmv: "video",
      flv: "video",
      webm: "video",
      mpeg: "video",
      mpg: "video",
      m4v: "video",
      apk: "apk",
      exe: "exe",
      "3gp": "video",
    };
    return typeMap[extension] || "file";
  };

  const handleDownload = async (url, fileName = "download", fileIndex) => {
    setDownloadingFiles((prev) => ({
      ...prev,
      [fileIndex]: true,
    }));
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {},
      });

      if (!response.ok) {
        throw new Error("File download failed");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.setAttribute("download", fileName);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingFiles((prev) => ({
        ...prev,
        [fileIndex]: false,
      }));
    }
  };

  const renderContent = () => {
    if (!text) return null;

    try {
      const delta = JSON.parse(text);
      if (Array.isArray(delta)) {
        return parseDelta(delta, id);
      }
    } catch (e) {
      return renderTextWithEmojis(text, `plain-${id}`);
    }

    return renderTextWithEmojis(text, `plain-${id}`);
  };

  const openImagePreview = (file) => {
    setSelectedImage(`${apiUrl}${file.file_url}`);
    setShowImageModal(true);
  };

  const closeImagePreview = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };
  const handleAvatarClick = () => {
    setShowPersonPopUp(true);
  };
  return (
    <div
      className={styles.ChatViewMessageContainer}
      style={{
        padding: !showHeader ? "4px 5px" : "",
      }}
    >
      <div className={styles.chatViewAllComp}>
        {showHeader ? (
          <div
            className={styles.ChatViewAvatar}
            onClick={handleAvatarClick}
            style={{ cursor: "pointer" }}
          >
            {imgError || !avatar ? (
              <FallbackAvatar name={userName} size={36} />
            ) : (
              <img
                src={avatar}
                alt={`${userName}'s avatar`}
                onError={() => setImgError(true)}
              />
            )}
          </div>
        ) : (
          <div className={styles.ChatViewAvatarSpacer}></div>
        )}

        <div className={styles.ChatViewMessageContent}>
          {showHeader && (
            <div className={styles.ChatViewHeader}>
              <span className={styles.ChatViewUsername}>{userName}</span>
              <span className={styles.ChatViewTime}>
                {" "}
                • {time.replace(/:\d{2}\s/, " ")}
              </span>
            </div>
          )}
          <div
            className={`${styles.ChatViewBubble} ${
              !showHeader ? styles.noHeader : ""
            }`}
          >
            {is_forward === "forward" && (
              <div className={styles.forwardHeader}>
                <PiShareFat className={styles.forwardIcon} />
                <span className={styles.forwardText}>Forwarded </span>
              </div>
            )}
            {is_pinned === "pinned" && (
              <div className={styles.pinnedIndicator}>
                <TfiPinAlt className={styles.pinnedIcon} />
                <span>Pinned</span>
              </div>
            )}
            {text && (
              <span className={styles.ChatViewUserTextChat}>
                {renderContent()}
              </span>
            )}
            {file?.length > 0 && (
              <div
                className={styles.filesContainer}
                style={{
                  gridTemplateColumns:
                    file.length === 1
                      ? "1fr"
                      : file.length === 2
                      ? "repeat(2, 1fr)"
                      : "repeat(3, 1fr)",
                }}
              >
                {file.map((file, index) => (
                  <div key={index} className={styles.fileItemWrapper}>
                    {["jpg", "jpeg", "png", "gif", "image/png"].includes(
                      file.file_extension.toLowerCase()
                    ) ? (
                      <div
                        className={`${styles.fileItem} ${
                          styles[getFileType(file.file_extension)]
                        }`}
                      >
                        <div
                          className={styles.fileIcon}
                          onClick={() => openImagePreview(file)}
                        >
                          <img
                            src={`${apiUrl}${file.file_url}`}
                            alt={file.file_extension}
                            className={styles.fileTypeImageIcon}
                          />
                        </div>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileName}>
                            {file.file_name}
                          </div>
                          {file.file_size && (
                            <div className={styles.fileSize}>
                              {formatFileSize(file.file_size)}
                            </div>
                          )}
                        </div>

                        {downloadingFiles[index] ? (
                          <div className={styles.downloadLoader}>
                            <div className={styles.downloadSpinner}></div>
                          </div>
                        ) : (
                          <GrDownload
                            className={styles.downloadButton}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              handleDownload(
                                `${apiUrl}${file.file_url}`,
                                file.file_name,
                                index
                              )
                            }
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        className={`${styles.fileItem} ${
                          styles[getFileType(file.file_extension)]
                        }`}
                      >
                        <div className={styles.fileIcon}>
                          <img
                            src={`Images/Icons/${getFileType(
                              file.file_extension
                            )}.png`}
                            alt={file.file_extension}
                            className={styles.fileTypeImageIcon}
                          />
                        </div>
                        <div className={styles.fileInfo}>
                          <div className={styles.fileName}>
                            {file.file_name}
                          </div>
                          {file.file_size && (
                            <div className={styles.fileSize}>
                              {formatFileSize(file.file_size)}
                            </div>
                          )}
                        </div>

                        {downloadingFiles[index] ? (
                          <div className={styles.downloadLoader}>
                            <div className={styles.downloadSpinner}></div>
                          </div>
                        ) : (
                          <GrDownload
                            className={styles.downloadButton}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              handleDownload(
                                `${apiUrl}${file.file_url}`,
                                file.file_name,
                                index
                              )
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            {transformedReactions.length > 0 && (
              <div className={styles.messageReactionsOuter}>
                {transformedReactions.map((reaction, index) => {
                  const isCurrentUserReacted = reaction.people.some(
                    (user) => user.register_id === currentUserId
                  );
                  const getFirstCodePoint = (emoji) => {
                    if (emoji.includes("\ufe0f")) {
                      return Array.from(emoji)
                        .map((char) => char.codePointAt(0).toString(16))
                        .join("-");
                    }

                    const chars = Array.from(emoji);
                    if (chars.length > 1) {
                      return chars
                        .map((char) => char.codePointAt(0).toString(16))
                        .join("-");
                    }

                    return chars[0].codePointAt(0).toString(16);
                  };
                  return (
                    <div
                      key={index}
                      className={`${styles.reactionBadgeOuter} ${
                        isCurrentUserReacted ? styles.userReacted : ""
                      }`}
                      onClick={() => toggleReaction(reaction.emoji)}
                      onMouseEnter={() => setHoveredReaction(reaction)}
                      onMouseLeave={() => setHoveredReaction(null)}
                    >
                      <span>
                        <Emoji
                          unified={getFirstCodePoint(reaction.emoji)}
                          size={14}
                        />
                      </span>
                      {reaction.count > 1 && (
                        <span style={{ color: "gray" }}>{reaction.count}</span>
                      )}
                      {hoveredReaction === reaction && (
                        <div className={styles.reactionTooltip}>
                          <div style={{ textAlign: "center" }}>
                            <Emoji
                              unified={getFirstCodePoint(reaction.emoji)}
                              size={25}
                            />
                          </div>
                          <div>
                            {reaction.people.map((person, idx) => (
                              <span key={person.register_id}>
                                {person.name}
                                {idx < reaction.people.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className={styles.reactionButtonOuter}>
          <BsEmojiSmile
            size={18}
            onClick={() => setShowReactionPicker(!showReactionPicker)}
          />
          <PiShareFat onClick={handleForwardClick} size={20} />
          {is_you === "You" && showDeleteButton && (
            <RiDeleteBinLine onClick={handleDeleteClick} />
          )}
          {is_pinned === "pinned" ? (
            <RiUnpinLine onClick={handlePinClick} size={20} />
          ) : (
            <TfiPinAlt onClick={handlePinClick} />
          )}
        </div>
        <div></div>

        {showReactionPicker && (
          <div className={styles.reactionPickerOuter}>
            {commonReactions.map((emoji, index) => (
              <div
                key={index}
                className={styles.reactionItem}
                onClick={() => toggleReaction(emoji)}
              >
                {emoji}
              </div>
            ))}
            <div
              className={styles.reactionItem}
              onClick={() => {
                setShowReactionPicker(false);
                setShowEmojiPopup(true);
              }}
            >
              <FaPlus />
            </div>
          </div>
        )}
        {showEmojiPopup && (
          <div className={styles.emojiPickerContainerViewChat}>
            <div
              className={styles.emojiPickerWrapper}
              onMouseLeave={() => setShowEmojiPopup(false)}
            >
              <EmojiPicker
                onEmojiClick={(emojiObject) => {
                  toggleReaction(emojiObject.emoji);
                  setShowEmojiPopup(false);
                }}
                width={300}
                height={400}
                skinTonesDisabled
                searchDisabled
              />
            </div>
          </div>
        )}
        {showImageModal && selectedImage && (
          <div className={styles.emojiPickerContainerViewChat}>
            <ImagePreview
              onClose={closeImagePreview}
              selectedImage={selectedImage}
            />
          </div>
        )}

        {showPersonPopUp && (
          <div className={styles.emojiPickerContainerViewChat}>
            <PersonQuickView
              userId={user_id}
              onClose={() => setShowPersonPopUp(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export default ChatView;
